import type {
    PerformanceSummaryDrilldownFilter,
    PerformanceSummaryOrderByItem,
} from './usePerformanceSummaryKpis';

export type PerformanceSummaryUserInput = {
    input_id: string;
    input_val: number | string;
};

export type PerformanceSummaryFeatureMeta = {
    feature_id: string;
    tab?: {
        id_type: string;
        id: string;
    };
};

export type KpiDeltaPage<TNode> = {
    nodes: TNode[];
    total?: number;
    offset: number;
    limit: number;
};

export type KpiTreeNode = {
    dimension?: string;
    attribute_type?: string;
    ex_id?: string;
    description?: string;
    is_total?: boolean;
    kpis?: Array<{ kpi_id?: string; [key: string]: unknown }>;
    data?: KpiTreeNode[];
};

type BuildKpiRequestBodyArgs = {
    kpiIds: string[];
    dimension?: string;
    aggregateAttributeTypes: string[];
    aggregateDimensions?: Array<string | undefined>;
    aggregateIsAttributes?: Array<boolean | undefined>;
    drilldownFilters?: PerformanceSummaryDrilldownFilter[];
    orderBy?: PerformanceSummaryOrderByItem[] | null;
    limit: number;
    offset: number;
    timePeriod: { start_timestamp?: number; end_timestamp?: number };
    filters: { dim1_id_list: unknown; dim2_id_list: unknown };
    userInputs?: PerformanceSummaryUserInput[];
    featureMeta?: PerformanceSummaryFeatureMeta;
};

export const buildKpiDeltaRequestBody = ({
    kpiIds,
    dimension,
    aggregateAttributeTypes,
    aggregateDimensions,
    aggregateIsAttributes,
    drilldownFilters,
    orderBy,
    limit,
    offset,
    timePeriod,
    filters,
    userInputs,
    featureMeta,
}: BuildKpiRequestBodyArgs) => ({
    kpi_ids: kpiIds,
    time_period: timePeriod,
    filters,
    aggregates: aggregateAttributeTypes.map((attributeType, index) => {
        const aggregateDimension = aggregateDimensions?.[index] ?? dimension;
        const aggregateIsAttribute = aggregateIsAttributes?.[index] ?? true;
        return {
            dimension: aggregateDimension,
            attribute_type: attributeType,
            is_attribute: aggregateIsAttribute,
        };
    }),
    ...(drilldownFilters ? { drilldown_filters: drilldownFilters } : {}),
    ...(orderBy?.length ? { order_by: orderBy } : {}),
    include_growth: true,
    limit,
    offset,
    ...(userInputs?.length ? { user_inputs: userInputs } : {}),
    ...(featureMeta ? { feature_meta: featureMeta } : {}),
});

export function parseTotalFromPayload(payload: unknown): number | undefined {
    if (!payload || typeof payload !== 'object') return undefined;
    const p = payload as Record<string, unknown>;
    const meta = p.meta as Record<string, unknown> | undefined;
    const candidates = [p.total, p.count, p.total_count, meta?.total, meta?.total_count];
    for (const c of candidates) {
        if (typeof c === 'number' && Number.isFinite(c)) return c;
        if (typeof c === 'string' && c.trim() !== '') {
            const n = Number(c);
            if (Number.isFinite(n)) return n;
        }
    }
    return undefined;
}

const treeNodeKey = (node: KpiTreeNode): string => {
    if (node.is_total) return `__total__|${node.attribute_type ?? ''}|${node.ex_id ?? ''}`;
    return `${node.attribute_type ?? ''}|${node.ex_id ?? ''}`;
};

/**
 * Merge KPI leaves from `deltaNodes` into `baseNodes`. Trees are anchor-driven
 * (identical row identities/order), so we match siblings by `treeNodeKey` and
 * recurse into `data`. Only KPI entries whose `kpi_id` is in `kpiIdsToAdd` are
 * appended; entries already present in baseNodes are skipped.
 */
export const mergeKpisFromDelta = (
    baseNodes: KpiTreeNode[],
    deltaNodes: KpiTreeNode[],
    kpiIdsToAdd: Set<string>,
): KpiTreeNode[] => {
    if (!deltaNodes.length) return baseNodes;
    const deltaByKey = new Map<string, KpiTreeNode>();
    deltaNodes.forEach((node) => {
        deltaByKey.set(treeNodeKey(node), node);
    });
    return baseNodes.map((baseNode) => {
        const matchingDeltaNode = deltaByKey.get(treeNodeKey(baseNode));
        const baseKpis = baseNode.kpis ?? [];
        const existingKpiIds = new Set(baseKpis.map((kpi) => kpi.kpi_id).filter(Boolean) as string[]);
        const extraKpis = (matchingDeltaNode?.kpis ?? []).filter(
            (kpi) => kpi.kpi_id && kpiIdsToAdd.has(kpi.kpi_id) && !existingKpiIds.has(kpi.kpi_id),
        );
        const mergedKpis = extraKpis.length ? [...baseKpis, ...extraKpis] : baseKpis;
        const mergedData = baseNode.data
            ? mergeKpisFromDelta(baseNode.data, matchingDeltaNode?.data ?? [], kpiIdsToAdd)
            : baseNode.data;
        if (mergedKpis === baseKpis && mergedData === baseNode.data) return baseNode;
        return { ...baseNode, kpis: mergedKpis, data: mergedData };
    });
};
